import React, { useState } from 'react';
import { forgotPassword } from '../api/apiService';
import { ToastSuccess, ToastError } from "../components/toast-notification";

function ForgotPassword() {
  const [formValues, setFormValues] = useState({ email: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'email') {
      if (!value) error = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(value)) error = 'Email address is invalid';
    }
    if (error) setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    let errors = {};
    if (!formValues.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formValues.email)) errors.email = 'Email address is invalid';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const tryForgotPassword = (bodyData) => {
    forgotPassword(bodyData?.email)
      .then(function (response) { ToastSuccess(response?.data?.message); setIsLoading(false); })
      .catch(function (error) { ToastError(error?.response?.data?.message); setIsLoading(false); });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) { setIsLoading(true); tryForgotPassword(formValues); }
  };

  return (
    <div className="min-h-screen flex overflow-hidden bg-[#030305]">

      {/* LEFT PANEL: Cyberpunk Visuals */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 border-r border-cyan-900/30">
        <div className="absolute inset-0 cyber-auth-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#030305]" />

        <div className="relative z-10 flex items-center gap-4 mb-auto">
          <div className="w-10 h-10 border border-cyan-400 bg-cyan-900/30 flex items-center justify-center" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}>
            <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <span className="text-2xl font-black text-cyan-400 tracking-widest uppercase drop-shadow-[0_0_8px_rgba(0,255,255,0.4)]">
            EXTENDSWARE
          </span>
        </div>

        <div className="relative z-10 my-auto">
          <h2 className="text-[4rem] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-fuchsia-500 drop-shadow-[0_0_15px_rgba(0,255,255,0.3)]">
            KEY
            <br />
            RECOVERY
          </h2>
          <div className="mt-6 flex flex-col gap-2 font-mono text-cyan-500/60 text-xs">
            <p> INITIATING KEY OVERRIDE PROTOCOL...</p>
            <p> VERIFYING OPERATOR IDENTIFIER...</p>
            <p> AWAITING EMAIL DESIGNATION...</p>
            <div className="h-4 w-32 bg-cyan-500/20 border border-cyan-500/30 mt-2 relative overflow-hidden">
              <div className="absolute top-0 left-0 h-full w-1/3 bg-cyan-500/50 animate-pulse" />
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-auto flex items-center justify-between text-[10px] font-mono text-cyan-900">
          <span>SYS.VER: 9.4.2</span>
          <span>ENCRYPTION: 256-BIT NEURAL</span>
        </div>
      </div>

      {/* RIGHT PANEL: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/10 via-[#030305] to-[#030305] lg:hidden" />

        <div className="w-full max-w-md cyber-card p-8 md:p-10 relative z-10 shadow-none border-none lg:bg-transparent lg:backdrop-filter-none">

          {/* Header */}
          <div className="mb-10">
            <h1 className="text-2xl font-black text-white tracking-widest uppercase drop-shadow-[0_0_8px_rgba(255,255,255,0.3)] mb-2">
              SYS.OVERRIDE_KEY
            </h1>
            <p className="cyber-label text-[10px]">
              PROVIDE IDENTIFIER TO INITIATE PASSWORD RESET SEQUENCE.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="cyber-label block mb-2">IDENTIFIER [EMAIL]</label>
              <input id="email" name="email" type="email" autoComplete="email"
                className={`cyber-input w-full p-3 font-mono ${formErrors.email ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="operator@sys.net" value={formValues.email} onChange={handleChange} onBlur={handleBlur} />
              {formErrors.email && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                [!] {formErrors.email}
              </p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading}
              className="cyber-btn w-full py-4 mt-2">
              {isLoading ? "TRANSMITTING..." : "SEND_RECOVERY_LINK"}
            </button>
          </form>

          <p className="mt-12 text-center text-[10px] font-bold uppercase tracking-widest text-gray-500">
            REMEMBERED YOUR KEY?{" "}
            <a href="/login" className="text-cyan-400 hover:text-cyan-300 drop-shadow-[0_0_5px_rgba(0,255,255,0.4)] ml-1">RETURN_TO_LOGIN</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
